/* =============================================================================
   VIEWS / DRIVER-TERMINAL — Fahrer-Terminal (S209 / phase_156)
   =============================================================================
   Mobile-first Vollbild-View für den Fahrer (Operator-Rolle `driver`). Zeigt
   NUR die eigenen zugewiesenen Lieferungen (list_driver_orders → Scope
   orders.driver_id = angemeldeter Fahrer-User), schaltet den Liefer-Status
   „Unterwegs → Zugestellt" fort und bietet Anruf- (tel:) + Navigations-
   Deep-Link (native Maps-App, kein API-Key).

   Muster (analog delivery.js/kds.js):
     - renderDriverTerminal()  → schreibt in #view-driver
     - enterDriverView()/exitDriverView() → body-Klasse `driver-active`,
       Hash-replaceState `#driver`, 30s-Fallback-Ticker
     - installDriverListenersOnce() → debounced order:*-Realtime, body-Klassen-
       Guard, KEIN off (Handler laufen nach exit leer)
     - Auto-Einstieg nach PIN-Login: operator-ui._submitPin schaltet bei
       Primär-Rolle 'driver' auf switchView('driver').

   Kein Trap (S168-Muster): der Operator-Chip in der Terminal-Topbar ist IMMER
   erreichbar (operatorOpenSwitch → Wechsel/Abmelden). Dark ist global (:root),
   KEINE Paper-Insel. Status-Buttons ohne Confirm-Dialog (Pilot-Operator-Stress-
   UX, Memory feedback_status_lifecycle_gates) — nur Inflight-Guard gegen
   Doppel-Tap. Der RPC update_delivery_status gate't den Driver-Scope hart.

   Pure Helper (Vitest): formatDriverAddress, buildMapsLink,
   driverActionForStatus, initialsFromName.
============================================================================ */

import { htmlEscape, showToast } from '../utils/helpers.js';

// -----------------------------------------------------------------------------
// Modul-State (überlebt Re-Renders; UI-Zustand nicht im DOM halten)
// -----------------------------------------------------------------------------
let _orders   = [];
let _loading  = true;
let _error    = null;
let _loadedAt = 0;
const _inflight = new Set();   // Order-Ids mit laufendem Status-Update
let _lastRenderSig = null;     // S209-HF2 (2) — Render-Signatur gegen DOM-Churn

// S210 — Geolocation-Sender-State.
let _geoWatchId = null;                 // navigator.geolocation.watchPosition-Handle
let _geoStatus  = 'off';                // 'off' | 'requesting' | 'active' | 'denied' | 'unavailable'
let _lastSent   = null;                 // { lat, lng, t } des letzten gesendeten Pings
let _lastGeoErrLogAt = 0;               // gedrosseltes Fehler-Logging (kein Funkloch-Spam)

const DRIVER_STATUS_LABEL = {
  assigned:         'Zugewiesen',
  out_for_delivery: 'Unterwegs'
};

// -----------------------------------------------------------------------------
// Pure Helper
// -----------------------------------------------------------------------------

/** Initialen aus einem Namen (Fallback, falls keine display_initials). */
export function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '–';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Adresse aus dem delivery_address-jsonb zu einer lesbaren Zeile. */
export function formatDriverAddress(addr) {
  if (!addr || typeof addr !== 'object') return '';
  const line1 = addr.street || '';
  const line2 = [addr.plz, addr.city].filter(Boolean).join(' ');
  return [line1, line2].filter(Boolean).join(', ');
}

/** Google-Maps-Deep-Link (öffnet die native Maps-App, kein API-Key). */
export function buildMapsLink(addr) {
  const q = formatDriverAddress(addr);
  if (!q) return '';
  return 'https://maps.google.com/?q=' + encodeURIComponent(q);
}

/** Status → nächste Fahrer-Aktion. Fahrer schaltet NUR assigned→out_for_delivery
 *  →delivered (Server-Scope in update_delivery_status). Kein pending/cancelled. */
export function driverActionForStatus(status) {
  if (status === 'assigned')         return { next: 'out_for_delivery', label: '🚗 Unterwegs',  kind: 'go' };
  if (status === 'out_for_delivery') return { next: 'delivered',        label: '✅ Zugestellt', kind: 'done' };
  return null;
}

/** S212 — Cent → „X,XX €". Pure. */
export function fmtEuroCents(cents) {
  const v = (Number(cents) || 0) / 100;
  return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/** S212 — Zahlungs-Kachel-Info aus der Order. Pure. paid → dezent (S213-B:
 *  „✓ Bar erhalten" bei payment_method='cash', sonst „✓ Online bezahlt");
 *  unpaid → prominent „💶 {total} bar einsammeln". */
export function driverPaymentInfo(o) {
  const paid = !!(o && o.payment_status === 'paid');
  if (paid) {
    const cash = (o && o.payment_method) === 'cash';
    return { paid: true, cls: 'paid', label: cash ? '✓ Bar erhalten' : '✓ Online bezahlt' };
  }
  return { paid: false, cls: 'unpaid', label: `💶 ${fmtEuroCents(o && o.total_gross_cents)} bar einsammeln` };
}

/** S212 — Primär-Aktion einer Karte (pure, testbar). Steuert Ready-Gate + den
 *  Bar-vor-Zugestellt-Fluss und gate't „Zugestellt" auf einen FERTIGEN Bon
 *  (sonst könnte die delivered-Mail keinen Beleg einbetten; zugleich Funkloch-
 *  Fallback):
 *   - assigned                        → „🚗 Unterwegs" (nur bei order_status='ready')
 *   - out_for_delivery + unpaid       → „💶 {total} Bar kassieren" (still fiskalisieren)
 *   - out_for_delivery + paid + !Bon  → „🧾 Beleg abschließen" (TSE+Bon nachholen, idempotent)
 *   - out_for_delivery + paid + Bon   → „✅ Zugestellt" */
export function driverPrimaryAction(o) {
  const st = o && o.delivery_status;
  const paid = !!(o && o.payment_status === 'paid');
  const hasBon = !!(o && o.has_bon);
  const ready = (o && o.order_status) === 'ready';
  if (st === 'assigned') {
    return { id: 'go', kind: 'go', next: 'out_for_delivery', label: '🚗 Unterwegs',
             disabled: !ready, hint: ready ? '' : '⏳ Küche/Theke noch nicht fertig' };
  }
  if (st === 'out_for_delivery') {
    if (!paid) return { id: 'cash', kind: 'cash', label: `💶 ${fmtEuroCents(o && o.total_gross_cents)} Bar kassieren` };
    if (!hasBon) return { id: 'cash', kind: 'cash', label: '🧾 Beleg abschließen' };
    return { id: 'deliver', kind: 'done', next: 'delivered', label: '✅ Zugestellt' };
  }
  return null;
}

/** S210 — Distanz zweier WGS84-Punkte in Metern (Haversine). Pure, testbar. */
export function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Erdradius m
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** S210 — Throttle-Entscheid: senden, wenn seit dem letzten Ping ≥ minMs
 *  vergangen sind ODER die Distanz ≥ minMeters ist (max alle 15 s ODER > 50 m).
 *  Ohne vorherigen Ping (prev=null) immer senden. Pure, testbar. */
export function shouldSendPosition(prev, next, now, opts) {
  const minMs = (opts && opts.minMs) || 15000;
  const minMeters = (opts && opts.minMeters) || 50;
  if (!prev) return true;
  if ((now - prev.t) >= minMs) return true;
  if (haversineMeters(prev.lat, prev.lng, next.lat, next.lng) >= minMeters) return true;
  return false;
}

// -----------------------------------------------------------------------------
// Operator-Zugriff
// -----------------------------------------------------------------------------
function getOp() {
  const api = window.App && window.App.operator;
  return (api && typeof api.getOperator === 'function') ? api.getOperator() : null;
}

// -----------------------------------------------------------------------------
// Render
// -----------------------------------------------------------------------------
function renderTopbar() {
  const op = getOp();
  const OR = window.OperatorRoles;
  if (!op) {
    return `<header class="dt-topbar">
      <div class="dt-title">🚗 Fahrer-Terminal</div>
      <button type="button" class="dt-op-chip" onclick="operatorOpenSwitch()">Anmelden</button>
    </header>`;
  }
  const roleLabel = OR && typeof OR.roleLabel === 'function' ? OR.roleLabel(op.role) : (op.role || '');
  const initials = op.initials ? op.initials : initialsFromName(op.name);
  return `<header class="dt-topbar">
    <div class="dt-title">🚗 Meine Lieferungen</div>
    <button type="button" class="dt-op-chip" onclick="operatorOpenSwitch()"
            title="Operator wechseln / abmelden">
      <span class="dt-op-badge">${htmlEscape(initials)}</span>
      <span class="dt-op-meta">
        <span class="dt-op-name">${htmlEscape(op.name || '')}</span>
        <span class="dt-op-role">${htmlEscape(roleLabel)}</span>
      </span>
    </button>
  </header>${_geoHintHtml()}`;
}

/** S210-HF2 — pure Banner-Modell (Vitest). NUR wenn aktive Lieferungen; sonst
 *  kein Banner. Aktiv = nicht-tappbarer Status; alle anderen Zustände sind ein
 *  TAPPBARER Button, weil iOS/Android den nativen Standort-Dialog nur in einer
 *  User-Geste (getCurrentPosition beim Tap) zuverlässig auslösen. */
export function geoBannerModel(geoStatus, hasOrders) {
  if (!hasOrders) return { show: false };
  if (geoStatus === 'active') {
    return { show: true, tappable: false, cls: 'dt-geo-on', text: '📡 Standort aktiv' };
  }
  const base = { show: true, tappable: true };
  switch (geoStatus) {
    case 'denied':
      return { ...base, cls: 'dt-geo-off', text: '📡 Standort aktivieren (tippen)',
               sub: 'Standort im Browser für diese Seite erlauben, dann hier tippen.' };
    case 'unavailable':
      return { ...base, cls: 'dt-geo-off', text: '📡 Kein GPS-Signal — erneut versuchen (tippen)' };
    case 'requesting':
      return { ...base, cls: 'dt-geo-wait', text: '📡 Standort wird ermittelt …' };
    default: // 'off'
      return { ...base, cls: 'dt-geo-wait', text: '📡 Standort aktivieren (tippen)' };
  }
}

/** S210-HF2 — pure Mapping GeolocationPositionError → Status. code 1 =
 *  PERMISSION_DENIED, sonst (2/3/…) = kein Signal/Timeout → weiter versuchbar. */
export function geoErrorToStatus(err) {
  return (err && err.code === 1) ? 'denied' : 'unavailable';
}

/** Header-Hinweis: aktiv = Div, sonst tappbarer Button (löst den nativen Dialog).
 *  Denied ist KEIN Fehler-Screen — das Terminal bleibt voll nutzbar (Statuskette
 *  funktioniert ohne GPS). Inline-Handler via func()-Muster (CSP). */
function _geoHintHtml() {
  const m = geoBannerModel(_geoStatus, !!(_orders && _orders.length));
  if (!m.show) return '';
  if (!m.tappable) return `<div class="dt-geo ${m.cls}">${htmlEscape(m.text)}</div>`;
  return `<button type="button" class="dt-geo dt-geo-btn ${m.cls}" onclick="driverActivateLocation()">
    <span class="dt-geo-txt">${htmlEscape(m.text)}</span>${m.sub ? `<span class="dt-geo-sub">${htmlEscape(m.sub)}</span>` : ''}
  </button>`;
}

function renderOrderCard(o) {
  const addr = (o.delivery_address && typeof o.delivery_address === 'object') ? o.delivery_address : {};
  const addrLine  = formatDriverAddress(addr);
  const floor     = addr.floor ? String(addr.floor) : '';
  const addrNotes = addr.driver_notes || addr.notes || '';
  const orderNotes = o.notes || '';
  const phone     = o.customer_phone || '';
  const mapsLink  = buildMapsLink(addr);
  const busy      = _inflight.has(o.id);
  const statusLabel = DRIVER_STATUS_LABEL[o.delivery_status] || o.delivery_status || '';
  const pay       = driverPaymentInfo(o);
  const action    = driverPrimaryAction(o);

  const callBtn = phone
    ? `<a class="dt-btn dt-btn-call" href="tel:${htmlEscape(phone)}">📞 Anrufen</a>`
    : '';
  const navBtn = mapsLink
    ? `<a class="dt-btn dt-btn-nav" href="${htmlEscape(mapsLink)}" target="_blank" rel="noopener">🗺 Navigation</a>`
    : '';

  // Primär-Aktion: go/deliver → driverAdvance, cash → driverCollectCash (still).
  let primaryBtn = '';
  if (action) {
    const disabled = busy || action.disabled;
    const handler = action.id === 'cash'
      ? `driverCollectCash('${htmlEscape(o.id)}')`
      : `driverAdvance('${htmlEscape(o.id)}','${action.next}')`;
    primaryBtn = `<button type="button" class="dt-btn dt-btn-${action.kind}" ${disabled ? 'disabled' : ''}
              onclick="${handler}">${busy ? '…' : htmlEscape(action.label)}</button>`
      + (action.hint && !busy ? `<div class="dt-gate-hint">${htmlEscape(action.hint)}</div>` : '');
  }
  // „Zustellung fehlgeschlagen" — verfügbar, sobald der Fahrer unterwegs ist.
  const failBtn = (o.delivery_status === 'out_for_delivery')
    ? `<button type="button" class="dt-btn dt-btn-fail" ${busy ? 'disabled' : ''}
              onclick="driverReportFailed('${htmlEscape(o.id)}')">✖ Zustellung fehlgeschlagen</button>`
    : '';

  return `
    <article class="dt-card dt-card-${htmlEscape(o.delivery_status || '')}">
      <div class="dt-card-head">
        <span class="dt-order-no">#${htmlEscape(o.order_number || '')}</span>
        <span class="dt-status dt-status-${htmlEscape(o.delivery_status || '')}">${htmlEscape(statusLabel)}</span>
      </div>
      <div class="dt-cust">${htmlEscape(o.customer_name || '—')}</div>
      <div class="dt-addr">📍 ${htmlEscape(addrLine || 'Keine Adresse')}${floor ? ` · ${htmlEscape(floor)}` : ''}</div>
      ${addrNotes ? `<div class="dt-note">📝 ${htmlEscape(addrNotes)}</div>` : ''}
      ${orderNotes ? `<div class="dt-note">🗒 ${htmlEscape(orderNotes)}</div>` : ''}
      <div class="dt-pay dt-pay-${pay.cls}">${htmlEscape(pay.label)}</div>
      <div class="dt-actions">
        ${callBtn}${navBtn}
      </div>
      ${primaryBtn ? `<div class="dt-actions dt-actions-main">${primaryBtn}</div>` : ''}
      ${failBtn ? `<div class="dt-actions dt-actions-fail">${failBtn}</div>` : ''}
    </article>`;
}

/** S209-HF2 (2) — pure Signatur des sichtbaren Zustands (Vitest). Gleiche Eingabe
 *  → gleiche Signatur → kein DOM-Austausch. */
export function driverRenderSignature(parts) {
  const p = parts || {};
  return JSON.stringify({
    l: !!p.loading, e: p.error || null,
    op: p.operator ? [p.operator.userId, p.operator.role, p.operator.name] : null,
    o: p.orders || [],
    f: p.inflight || [],
    g: p.geoStatus || 'off'   // S210 — Geo-Status im Header (Signatur, kein Churn)
  });
}

/** Signatur des aktuellen Modul-Zustands. Ändert sie sich nicht, wird der DOM
 *  NICHT ausgetauscht → keine verfallenden Element-Refs, keine verschluckten Taps
 *  auf dem Fahrer-Handy. Strukturelle Re-Renders nur bei echter Daten-/Zustands-
 *  Änderung (Realtime-Event, Inflight, Operator). */
function _renderSig() {
  return driverRenderSignature({
    loading: _loading, error: _error,
    operator: getOp(),
    orders: _orders,
    inflight: Array.from(_inflight),
    geoStatus: _geoStatus
  });
}

export function renderDriverTerminal() {
  const root = document.getElementById('view-driver');
  if (!root) return;

  // S209-HF2 (2) — nur neu zeichnen, wenn sich wirklich etwas geändert hat (oder
  // der Root noch leer ist). Verhindert den Sekundentakt-DOM-Austausch.
  const sig = _renderSig();
  if (sig === _lastRenderSig && root.querySelector('.dt-topbar')) return;
  _lastRenderSig = sig;

  // Scroll-Position über den innerHTML-Swap retten (non-destruktiver Render).
  const prevScroll = root.querySelector('.dt-scroll');
  const scrollTop = prevScroll ? prevScroll.scrollTop : 0;

  let body;
  if (_loading && _orders.length === 0) {
    body = `<div class="dt-empty">Lade Lieferungen…</div>`;
  } else if (_error) {
    body = `<div class="dt-empty dt-error">Konnte Lieferungen nicht laden.
      <div class="dt-empty-sub">${htmlEscape(_error)}</div>
      <button type="button" class="dt-btn dt-btn-go" onclick="driverReloadOrders()" style="margin-top:14px;">Erneut versuchen</button>
    </div>`;
  } else if (_orders.length === 0) {
    body = `<div class="dt-empty">✅ Keine Lieferungen zugewiesen.
      <div class="dt-empty-sub">Neue Aufträge erscheinen hier automatisch — kein Neuladen nötig.</div>
    </div>`;
  } else {
    body = `<div class="dt-list">${_orders.map(renderOrderCard).join('')}</div>`;
  }

  root.innerHTML = `${renderTopbar()}<div class="dt-scroll">${body}</div>`;

  const nextScroll = root.querySelector('.dt-scroll');
  if (nextScroll && scrollTop) nextScroll.scrollTop = scrollTop;
}

// -----------------------------------------------------------------------------
// Daten
// -----------------------------------------------------------------------------
async function loadDriverOrders(force = false) {
  const age = Date.now() - _loadedAt;
  if (!force && _orders.length > 0 && age < 15000) { renderDriverTerminal(); _syncGeoTracking(); return; }
  _error = null;
  try {
    const storage = window.App && window.App.storage && window.App.storage.orders;
    if (!storage || typeof storage.listDriverOrders !== 'function') {
      _orders = [];
    } else {
      _orders = await storage.listDriverOrders();
    }
    _loadedAt = Date.now();
  } catch (e) {
    // S209-HF2 (3) — Rollen-Wechsel-Race: ist der aktuelle Operator kein reiner
    // Fahrer mehr (oder die View bereits verlassen), den Fehler STILL schlucken —
    // kein roter „OPERATOR_FORBIDDEN"-Flash, während die App ohnehin wegrouted.
    const op = getOp();
    if (!op || op.role !== 'driver' || (typeof document !== 'undefined' && !document.body.classList.contains('driver-active'))) {
      return;
    }
    console.warn('[driver-terminal] loadDriverOrders:', e);
    _error = (e && (e.message || String(e))) || 'Unbekannter Fehler';
    _orders = [];
  }
  _loading = false;
  renderDriverTerminal();
  _syncGeoTracking();   // S210 — Tracking an Order-Lage angleichen (start/stop)
}

/** Reload-Handler (Inline-onclick im Fehler-Zustand). */
export function driverReloadOrders() {
  _loading = _orders.length === 0;
  renderDriverTerminal();
  loadDriverOrders(true);
}

/** Fahrer schaltet den Liefer-Status seiner eigenen Order fort. Ohne Confirm
 *  (Stress-UX), aber mit Inflight-Guard gegen Doppel-Tap. Der Server (RPC
 *  update_delivery_status, Driver-Scope) erzwingt Rechte + erlaubte Übergänge. */
export async function driverAdvance(orderId, nextStatus) {
  if (!orderId || _inflight.has(orderId)) return;
  _inflight.add(orderId);
  renderDriverTerminal();
  try {
    const storage = window.App && window.App.storage && window.App.storage.orders;
    if (!storage || typeof storage.updateDeliveryStatus !== 'function') {
      throw new Error('Status-API nicht verfügbar.');
    }
    await storage.updateDeliveryStatus(orderId, nextStatus);
    showToast(nextStatus === 'delivered' ? 'Als zugestellt markiert. Danke! 🙌' : 'Unterwegs — gute Fahrt! 🚗', 'success');
    await loadDriverOrders(true);
  } catch (e) {
    console.error('[driver-terminal] driverAdvance:', e);
    showToast('Konnte nicht aktualisieren: ' + (e && (e.message || e)), 'error');
  } finally {
    _inflight.delete(orderId);
    renderDriverTerminal();
  }
}

/** S212 — Bar kassieren: die volle GoBD-Kette (Zahlung + TSE + Bon) STILL im
 *  Hintergrund — kein Modal, kein Auto-Print. Idempotent (markPaid/TSE/Bon sind
 *  wiederhol-sicher), daher ist „nochmal tippen" bei Funkloch-Fehler unkritisch.
 *  Bei Fehler NICHT weiterschalten (kein delivered ohne Bon). */
export async function driverCollectCash(orderId) {
  if (!orderId || _inflight.has(orderId)) return;
  // S213-B — Bar-Confirm vor dem Kassieren (Memory inline_over_native_dialogs).
  // Nur der GoBD-scharfe Bar-Fall (unpaid) fragt; „Beleg abschließen" (paid ohne
  // Bon, Funkloch-Retry) läuft ohne Rückfrage durch.
  const ord = (_orders || []).find(o => o && o.id === orderId);
  const isUnpaid = !!(ord && ord.payment_status !== 'paid');
  if (isUnpaid && typeof window.styledConfirm === 'function') {
    const total = fmtEuroCents(ord && ord.total_gross_cents);
    const res = await window.styledConfirm({
      title: `${total} bar erhalten?`,
      body: 'Bestätige, dass du das Bargeld eingesammelt hast — der Beleg wird sofort fiskalisiert.',
      confirmLabel: 'Ja, bar erhalten',
      cancelLabel: 'Abbrechen'
    });
    if (!res || !res.confirmed) return;   // Abbruch → nicht kassieren, Status bleibt
  }
  if (_inflight.has(orderId)) return;
  _inflight.add(orderId);
  renderDriverTerminal();
  try {
    await _driverFiscalizeCash(orderId);
    showToast('Bar kassiert ✓', 'success');
    await loadDriverOrders(true);
  } catch (e) {
    console.error('[driver-terminal] driverCollectCash:', e);
    showToast('Kassieren fehlgeschlagen — bitte nochmal tippen: ' + (e && (e.message || e)), 'error');
  } finally {
    _inflight.delete(orderId);
    renderDriverTerminal();
  }
}

/** Reuse der bestehenden Cockpit-Bausteine (markPaid-RPC + tseSignAndRecord +
 *  _ensureBonArchived) — dieselbe GoBD-Kette wie im Cockpit, nur ohne UI-Lärm. */
async function _driverFiscalizeCash(orderId) {
  const storage = window.App && window.App.storage && window.App.storage.orders;
  if (!storage || typeof storage.markPaid !== 'function') throw new Error('Storage nicht verfügbar.');
  // 1) Zahlung + Rechnungsnummer (driver-scoped RPC). Idempotent: schon paid → returnt.
  const updated = await storage.markPaid(orderId, 'cash', null);
  if (!updated) throw new Error('Bezahl-Markierung ohne Ergebnis.');
  // 2) TSE-Signatur (GoBD) — muss gelingen.
  if (typeof window.tseSignAndRecord === 'function') {
    await window.tseSignAndRecord(updated);
  }
  // 3) Bon rendern + archivieren (bon_archive.html) — frische Order + Positionen.
  let fresh = (typeof storage.getFresh === 'function' ? await storage.getFresh(orderId).catch(() => null) : null) || updated;
  try {
    const items = (typeof storage.listItems === 'function') ? await storage.listItems(orderId) : [];
    if (items && items.length) {
      fresh = { ...fresh, preorderSnapshot: { version: 2, items: items.map(it => ({
        name:             it.nameSnapshot || it.name_snapshot,
        qty:              it.qty,
        unit_price_cents: it.unitPriceCents || it.unit_price_cents,
        line_total_cents: it.lineTotalCents || it.line_total_cents,
        tax_rate:         it.taxRate || it.tax_rate,
        note:             it.notes || (it.customizations && it.customizations.note)
      })) } };
    }
  } catch (_) { /* Bon zeigt ggf. „Keine Positionen" — nicht fatal */ }
  if (typeof window._ensureBonArchived !== 'function') throw new Error('Bon-Archivierung nicht verfügbar.');
  const res = await window._ensureBonArchived(fresh, { source: 'driver-terminal' });
  if (!res || !res.archived) throw new Error(res && res.error ? res.error : 'Bon-Archivierung fehlgeschlagen.');
}

/** S212 — „Zustellung fehlgeschlagen": Grund abfragen (styled), report_delivery_
 *  failed → Order verschwindet aus dem Terminal, Manager übernimmt im Cockpit. */
export async function driverReportFailed(orderId) {
  if (!orderId || _inflight.has(orderId)) return;
  let reason = '';
  try {
    const r = await window.styledPrompt({
      title: 'Zustellung fehlgeschlagen',
      body: 'Bitte gib einen Grund an (z. B. „Kunde nicht angetroffen", „Adresse falsch"). Die Lieferung geht dann zurück an die Disposition.',
      placeholder: 'Grund der Nicht-Zustellung',
      confirmLabel: 'Als fehlgeschlagen melden', danger: true
    });
    if (!r || !r.confirmed) return;
    reason = (r.value || '').trim();
  } catch (_) { return; }
  if (!reason) { showToast('Bitte einen Grund angeben.', 'error'); return; }
  _inflight.add(orderId);
  renderDriverTerminal();
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const sid = (window.App.operator && typeof window.App.operator.getActiveSessionId === 'function')
      ? window.App.operator.getActiveSessionId() : null;
    const { error } = await supa.rpc('report_delivery_failed', {
      p_order_id: orderId, p_reason: reason, p_operator_session_id: sid
    });
    if (error) throw error;
    showToast('Als fehlgeschlagen gemeldet — Manager übernimmt.', 'info');
    await loadDriverOrders(true);
  } catch (e) {
    console.error('[driver-terminal] driverReportFailed:', e);
    showToast('Melden fehlgeschlagen: ' + (e && (e.message || e)), 'error');
  } finally {
    _inflight.delete(orderId);
    renderDriverTerminal();
  }
}

// -----------------------------------------------------------------------------
// Geolocation-Sender (S210) — NUR bei aktiver Lieferung, gedrosselt, Fehler still.
// -----------------------------------------------------------------------------

/** Pure (Vitest): Sende-Gate — getrackt wird NUR bei ≥ 1 offenen eigenen Order
 *  (list_driver_orders liefert genau assigned/out_for_delivery). */
export function shouldTrackLocation(orders) {
  return Array.isArray(orders) && orders.length > 0;
}

/** Startet/stoppt das Tracking je nach Order-Lage. Kein Tracking im Leer-Zustand. */
function _syncGeoTracking() {
  if (shouldTrackLocation(_orders)) _startGeoWatch();
  else _stopGeoWatch();
}

function _startGeoWatch() {
  if (_geoWatchId !== null) return;              // läuft schon
  if (_geoStatus === 'denied') return;           // nach Ablehnung nicht weiter nerven
  if (typeof document !== 'undefined' && !document.body.classList.contains('driver-active')) return;
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    _setGeoStatus('unavailable');
    return;
  }
  _setGeoStatus('requesting');
  try {
    _geoWatchId = navigator.geolocation.watchPosition(_onGeoPos, _onGeoErr, {
      enableHighAccuracy: true, maximumAge: 10000, timeout: 20000
    });
  } catch (_) {
    _setGeoStatus('unavailable');
  }
}

function _stopGeoWatch() {
  if (_geoWatchId !== null && typeof navigator !== 'undefined' && navigator.geolocation) {
    try { navigator.geolocation.clearWatch(_geoWatchId); } catch (_) { /* ignore */ }
  }
  _geoWatchId = null;
  _lastSent = null;
  // 'denied' bewusst NICHT zurücksetzen (Hinweis bleibt, solange Orders da sind).
  if (_geoStatus !== 'denied') _setGeoStatus('off');
}

function _setGeoStatus(s) {
  if (_geoStatus === s) return;
  _geoStatus = s;
  renderDriverTerminal();   // Signatur-gegatet → nur der Header-Hinweis ändert sich
}

function _onGeoPos(pos) {
  if (typeof document !== 'undefined' && !document.body.classList.contains('driver-active')) {
    _stopGeoWatch();
    return;
  }
  const c = pos && pos.coords;
  if (!c || typeof c.latitude !== 'number' || typeof c.longitude !== 'number') return;
  _setGeoStatus('active');
  const next = { lat: c.latitude, lng: c.longitude, t: Date.now() };
  if (!shouldSendPosition(_lastSent, next, next.t)) return;   // Throttle 15 s / 50 m
  const payload = {
    lat: c.latitude, lng: c.longitude,
    accuracy: (typeof c.accuracy === 'number') ? c.accuracy : null,
    heading:  (typeof c.heading === 'number' && !Number.isNaN(c.heading)) ? c.heading : null,
    speed:    (typeof c.speed === 'number' && !Number.isNaN(c.speed)) ? c.speed : null
  };
  _lastSent = next;   // optimistisch merken (verhindert Ping-Flut)
  const storage = window.App && window.App.storage && window.App.storage.driverLocations;
  if (!storage || typeof storage.update !== 'function') return;
  storage.update(payload).catch((e) => {
    _lastSent = null;   // Fehlversuch → nächsten Fix wieder senden
    _throttledGeoLog('update_driver_location', e);
  });
}

function _onGeoErr(err) {
  if (geoErrorToStatus(err) === 'denied') {   // PERMISSION_DENIED
    _stopGeoWatch();                           // Auto-Loop nicht weiter versuchen (kein Nag)
    _setGeoStatus('denied');                   // Banner wird zum „aktivieren (tippen)"-Button
    return;
  }
  // POSITION_UNAVAILABLE (2) / TIMEOUT (3): watchPosition versucht selbst weiter.
  _setGeoStatus('unavailable');
  _throttledGeoLog('geolocation', err);
}

/** S210-HF2 — expliziter User-Tap auf den Banner. Der einzige verlässliche Weg,
 *  den nativen Standort-Dialog auf iOS/Android auszulösen: getCurrentPosition
 *  DIREKT in der Geste. Re-armt IMMER (denied/off zurücksetzen) — der User hat
 *  evtl. gerade in den Browser-Settings freigegeben. Bei Erfolg sofort senden +
 *  Watch starten; bei Ablehnung denied. */
export function driverActivateLocation() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    _setGeoStatus('unavailable');
    return;
  }
  _setGeoStatus('requesting');   // setzt denied/off zurück → _startGeoWatch darf gleich starten
  try {
    navigator.geolocation.getCurrentPosition(
      (pos) => { _onGeoPos(pos); _startGeoWatch(); },
      (err) => { _onGeoErr(err); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
  } catch (_) {
    _setGeoStatus('unavailable');
  }
}

/** S210-HF2 — Permission-Change beobachten (iOS ≥16, Android/Chrome): gibt der
 *  User nachträglich in den Settings frei, startet das Tracking OHNE Reload. */
let _permWatched = false;
function _watchGeoPermission() {
  if (_permWatched) return;
  if (typeof navigator === 'undefined' || !navigator.permissions || !navigator.permissions.query) return;
  _permWatched = true;
  try {
    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      const onChange = () => {
        if (status.state === 'granted'
            && (typeof document === 'undefined' || document.body.classList.contains('driver-active'))
            && shouldTrackLocation(_orders)) {
          if (_geoStatus === 'denied') _geoStatus = 'off';   // re-arm für _startGeoWatch
          _startGeoWatch();
        } else if (status.state === 'denied') {
          _setGeoStatus('denied');
        }
      };
      status.onchange = onChange;
      onChange();   // initialer Abgleich
    }).catch(() => { /* permissions.query nicht verfügbar */ });
  } catch (_) { /* ignore */ }
}

/** Fehler still + gedrosselt (max. alle 60 s) loggen — kein Funkloch-Spam. */
function _throttledGeoLog(where, err) {
  const now = Date.now();
  if (now - _lastGeoErrLogAt < 60000) return;
  _lastGeoErrLogAt = now;
  console.warn(`[driver-terminal] ${where}:`, err && (err.message || err.code || err));
}

// -----------------------------------------------------------------------------
// Realtime-Listeners (einmalig; body-Klassen-Guard, kein off)
// -----------------------------------------------------------------------------
let _listenersInstalled = false;
function installDriverListenersOnce() {
  if (_listenersInstalled) return;
  _listenersInstalled = true;
  _watchGeoPermission();   // S210-HF2 — nachträgliches „Erlauben" ohne Reload greifen lassen
  const App = window.App;
  if (!App || !App.events) return;
  let _timer = null;
  function schedule() {
    if (!document.body.classList.contains('driver-active')) return;
    if (_timer) return;
    _timer = setTimeout(() => {
      _timer = null;
      if (document.body.classList.contains('driver-active')) loadDriverOrders(true);
    }, 200);
  }
  App.events.on('order:realtime',          schedule);
  App.events.on('order:status_changed',    schedule);
  App.events.on('order:updated',           schedule);
  App.events.on('order:created',           schedule);
  App.events.on('order:cancelled',         schedule);
  App.events.on('order:delivery_assigned', schedule);
  App.events.on('view:became-visible', (p) => {
    if (p && p.view === 'driver') loadDriverOrders(true);
  });
  // S209-HF2 (3) — Operator-Wechsel. Der Payload ist der neue Operator (oder null
  // beim Abmelden). Ist er KEIN reiner Fahrer mehr, das Terminal SOFORT verlassen
  // (Route zuerst!) und NICHT laden — sonst feuert list_driver_orders gegen die
  // neue Rolle → OPERATOR_FORBIDDEN-Flash. Anderer Fahrer → frische gescopte Liste.
  App.events.on('operator:changed', (op) => {
    if (!document.body.classList.contains('driver-active')) return;
    if (!op || op.role !== 'driver') {
      if (typeof window.switchView === 'function') window.switchView('admin');
      return;
    }
    loadDriverOrders(true);
  });
}

// -----------------------------------------------------------------------------
// Lifecycle
// -----------------------------------------------------------------------------
// S209-HF2 (2) — KEIN periodischer Tick mehr: das Terminal zeigt keine Relativ-
// zeiten, also gibt es nichts sekündlich zu aktualisieren. Strukturelle Updates
// kommen ausschließlich über die orders-Realtime-Events (installDriverListenersOnce),
// und selbst die zeichnen dank Render-Signatur nur bei echter Änderung neu.

export async function enterDriverView() {
  if (typeof document === 'undefined') return;
  document.body.classList.add('driver-active');
  installDriverListenersOnce();
  _loading = _orders.length === 0;
  renderDriverTerminal();
  await loadDriverOrders(true);
  try {
    const h = window.location.hash || '';
    if (!h.includes('#driver')) window.history.replaceState(null, '', '#driver');
  } catch (_) { /* ignore */ }
}

export function exitDriverView() {
  if (typeof document === 'undefined') return;
  document.body.classList.remove('driver-active');
  _stopGeoWatch();   // S210 — Tracking beim Verlassen des Terminals beenden
  try {
    const h = window.location.hash || '';
    if (h.includes('#driver')) window.history.replaceState(null, '', h.replace('#driver', ''));
  } catch (_) { /* ignore */ }
}

// -----------------------------------------------------------------------------
// Bridge auf window — für Inline-onclick-Handler (Memory feedback_gg_inline_handler_csp)
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderDriverTerminal,
    enterDriverView,
    exitDriverView,
    driverAdvance,
    driverReloadOrders,
    driverActivateLocation,  // S210-HF2 — Banner-Tap löst den Standort-Dialog aus
    driverCollectCash,       // S212 — Bar kassieren (stille GoBD-Kette)
    driverReportFailed       // S212 — Zustellung fehlgeschlagen (+Grund)
  });
}
